import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, Input } from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { useCustomerPatchMutation } from 'hooks/mutations/Customers/useCustomerMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { EmbeddedCustomerPatchPayload } from 'pages/EmbeddedCustomers/helpers/types'
import { CUSTOMER_API_URL } from 'services/CustomerService'

export interface ExternalReferenceFormProps {
  customerReference: string
  initialExternalReference: string
  onClose: () => void
  onSuccess: () => void
}

export const ExternalReferenceForm = ({
  customerReference,
  initialExternalReference,
  onClose,
  onSuccess,
}: ExternalReferenceFormProps) => {
  const { t } = useTranslation()
  const { requestError, handleRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: patchCustomer, isPending } =
    useCustomerPatchMutation<EmbeddedCustomerPatchPayload>()

  const [externalReference, setExternalReference] = useState(initialExternalReference || '')

  const onSave = () => {
    resetRequestError()
    if (externalReference === initialExternalReference) {
      onClose()
      return
    }
    patchCustomer(
      { customerReference, data: { externalReference: externalReference || null } },
      {
        onSuccess,
        onError: error =>
          handleRequestError(error, CUSTOMER_API_URL, 'patchEmbeddedCustomer', true),
      }
    )
  }

  return (
    <Dialog
      open
      type="utility"
      onClose={onClose}
      title={t('customers.externalReference')}
      ActionButtons={[
        <Button key="save" variant="primary" loading={isPending} onClick={onSave}>
          {t('save')}
        </Button>,
        <Button key="cancel" variant="secondary" disabled={isPending} onClick={onClose}>
          {t('cancel')}
        </Button>,
      ]}
    >
      <>
        {requestError?.show && <RequestErrorCallout requestError={requestError} />}
        <p className="text-body-regular text-foreground-subdued">
          {t('customers.externalReferenceTooltip')}
        </p>
        <Input
          value={externalReference}
          onChange={({ target: { value = '' } }) => setExternalReference(value)}
        />
      </>
    </Dialog>
  )
}
